import { Helmet } from 'react-helmet-async';
import { memo, useEffect, useState } from 'react';
import { Button, HelpInfo, Typography } from 'components';
import ClockIcon from 'assets/icons/clock.svg?react';
import styles from './ManualReview.module.scss';
import CardWithIcon from 'components/CardWithIcon/CardWithIcon';
import { useApplicationQuery } from 'hooks/webapi';
import useNavigateByApplication from 'pages/Waiting/useNavigateByApplication';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import { isDevEnv } from 'utils/isDevEnv';
import { useTranslation } from 'react-i18next';

const ManualReview = memo(function PSD2VerifiedMemo() {
  const { t } = useTranslation();
  const { navigateNext } = useNavigateByApplication();
  const [isPending, setPending] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const [isApplicationResolved, setIsApplicationResolved] = useState(false);
  const { data: checkStatusData } = useClientApplicationCheckStatusQuery(
    isPolling,
    5000,
  );
  const { data: applicationData } = useApplicationQuery(
    checkStatusData?.completed,
  );

  useEffect(() => {
    if (checkStatusData?.completed) {
      setIsPolling(false);

      if (
        applicationData?.status === 'OPEN' &&
        applicationData?.resolution !== 'MANUAL'
      ) {
        setIsApplicationResolved(true);
      }
    }
  }, [checkStatusData?.completed]);

  const handleClick = async () => {
    setPending(true);
    if (applicationData) {
      navigateNext(applicationData);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {' '}
          {t(
            isApplicationResolved
              ? 'manual_review:html_title_resolved'
              : 'manual_review:html_title',
          )}
        </title>
      </Helmet>

      {isDevEnv() && (
        <Button
          size="m"
          className={styles['resolved-trigger']}
          onClick={() => setIsApplicationResolved((prev) => !prev)}
          dataTest="manualReview-btn"
        >
          Trigger resolution
        </Button>
      )}

      <CardWithIcon
        icon={<ClockIcon />}
        dataTest="manual-review-card"
        heading={
          <Typography
            className={styles.header}
            size="l"
            kind="h1"
            dataTest="manual-review-heading"
          >
            {t(
              isApplicationResolved
                ? 'manual_review:heading_resolved'
                : 'manual_review:heading',
            )}
          </Typography>
        }
      >
        {isApplicationResolved ? (
          <div className={styles['box-wrapper']} data-test="manual-review-box">
            <Button
              className={styles.button}
              pending={isPending}
              onClick={handleClick}
              dataTest="psd2-verified"
            >
              {t('manual_review:navigate')}
            </Button>
          </div>
        ) : (
          <Typography
            className={styles.margin}
            dataTest="manual-review-description"
          >
            <span> {t('manual_review:description')}</span>
          </Typography>
        )}
      </CardWithIcon>
      <HelpInfo
        dataTest="manual-review-help-info"
        title={t('help_info:title')}
        text={t('help_info:text')}
      />
    </>
  );
});

export default ManualReview;
